import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, Box, TextField, Button,
  Alert, CircularProgress, useTheme, IconButton, Link
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { apiFetch } from '../apiFetch';

interface DecodedToken {
  first_name: string;
  last_name: string;
  email: string;
}

const ContactUs: React.FC = () => {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const navigate = useNavigate(); // Hook to navigate programmatically
  const theme = useTheme(); // Access the current theme (light or dark mode)

  const siteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY;
  const apiUrl = process.env.REACT_APP_API_URL;

  // Prefill fields if the user is logged in
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      // Cast jwtDecode to a callable function
      const decodeFn = jwtDecode as unknown as (token: string) => DecodedToken;
      const decoded: DecodedToken = decodeFn(token);
      setName(`${decoded.first_name} ${decoded.last_name}`);
      setEmail(decoded.email);
    }
  }, []);

  // Handle form submission to send the contact message
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFeedback(null);
    setIsSubmitting(true);

    if (!recaptchaToken) {
      setFeedback({ type: 'error', text: 'Please verify that you are not a robot.' });
      window.scrollTo(0, 0); // Scroll to the top to display the feedback message
      setIsSubmitting(false);
      return;
    }

    try {
      await apiFetch(`${apiUrl}/api/contact-us/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, message, recaptchaToken }),
      });
      setMessageSent(true);
      window.scrollTo(0, 0);
    } catch (err: any) {
      setFeedback({ type: 'error', text: err.message || 'Submission failed due to network error.' });
      window.scrollTo(0, 0); // Scroll to the top to display the feedback message
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setName('');
    setEmail('');
    setSubject('');
    setMessage('');
    setRecaptchaToken(null);
  };

  const handleCancel = () => { navigate('/') };

  // Success page after message is sent
  if (messageSent) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Message Sent!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Thank you for contacting us. We'll get back to you as soon as possible.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/')}
            sx={{ px: 4 }}
          >
            Continue
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box className="page-header" sx={{ mb: 2 }}>
          <Box className="title-row">
            <IconButton onClick={() => navigate('/')} aria-label="back to home">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" className="page-title">Contact Us</Typography>
          </Box>
        </Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Have a question or feedback? Please fill out the form below and we'll get back to you as soon as possible.
        </Typography>
        {feedback && (<Alert severity={feedback.type} sx={{ mb: 2 }}>{feedback.text}</Alert>)}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Name"
            variant="outlined"
            fullWidth
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <TextField
            label="Email"
            variant="outlined"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }}
            required
          />
          <TextField
            label="Subject"
            variant="outlined"
            fullWidth
            margin="normal"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
          />
          <TextField
            label="Message"
            variant="outlined"
            fullWidth
            margin="normal"
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
          />
          <Box sx={{ mt: 2, mb: 2 }}>
            <ReCAPTCHA
              key={theme.palette.mode} // Re-render the widget when the theme changes
              sitekey={siteKey}
              theme={theme.palette.mode}
              onChange={(token) => setRecaptchaToken(token)}
              onExpired={() => setRecaptchaToken(null)}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Protected by reCAPTCHA:{' '}
              <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" color="text.secondary" underline="hover">
                Privacy
              </Link>
              {' · '}
              <Link href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" color="text.secondary" underline="hover">
                Terms
              </Link>
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            fullWidth
            sx={{ mt: 2 }}
            disabled={isSubmitting} // Disable the button while submitting
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Submitting...' : 'Send Message'}
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 1 }}>
            <Button variant="outlined" onClick={handleClear} color="warning" sx={{ flex: 1 }}>Clear Form</Button>
            <Button variant="outlined" onClick={handleCancel} sx={{ flex: 1 }}>Cancel</Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ContactUs;